import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, Done, Cancel } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import useConfirmDialog from "services/hooks/useConfirmDialog";

const SCOPES = [
  { value: "view_contacts", label: "View contacts" },
  { value: "view_calls", label: "View call logs" },
  { value: "play_recording", label: "Play recordings" },
  { value: "edit_contacts", label: "Edit contacts" },
];

export default function CRMAccessGrantsManager() {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const headers = { Authorization: `Token ${token}` };
  const { ConfirmDialog, showConfirmDialog } = useConfirmDialog();

  const [grants, setGrants] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    grantee: "",
    target_agent: "",
    scopes: [],
    expires_at: "",
  });

  const loadGrants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${domain}crm/crm-access-grants/`, { headers });
      setGrants(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch (err) {
      console.error("Failed to load access grants:", err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, token]);

  const loadMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${domain}crm/crm-members/active/?company_id=${requestCompanyId}`, { headers });
      setMembers(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch (err) {
      console.error("Failed to load CRM members:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, token, requestCompanyId]);

  useEffect(() => {
    loadGrants();
    loadMembers();
  }, [loadGrants, loadMembers]);

  const openAddDialog = () => {
    setForm({ grantee: "", target_agent: "", scopes: [], expires_at: "" });
    setOpenDialog(true);
  };

  const toggleScope = (value) => {
    setForm((prev) => ({
      ...prev,
      scopes: prev.scopes.includes(value)
        ? prev.scopes.filter((s) => s !== value)
        : [...prev.scopes, value],
    }));
  };

  const handleSave = async () => {
    if (!form.grantee || !form.target_agent) {
      toast.error("Select both a grantee and a target agent.");
      return;
    }
    if (form.grantee === form.target_agent) {
      toast.error("Grantee and target agent cannot be the same person.");
      return;
    }
    if (form.scopes.length === 0) {
      toast.error("Select at least one scope to grant.");
      return;
    }
    try {
      await Promise.all(
        form.scopes.map((scope) =>
          axios.post(
            `${domain}crm/crm-access-grants/`,
            {
              grantee: form.grantee,
              target_agent: form.target_agent,
              scope,
              expires_at: form.expires_at || null,
            },
            { headers }
          )
        )
      );
      setOpenDialog(false);
      loadGrants();
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        "Failed to save one or more grants.";
      toast.error(message);
    }
  };

  const handleRevoke = (grant) => {
    showConfirmDialog({
      title: "Revoke Access Grant",
      description: `Revoke "${grant.scope}" access for ${grant.grantee_name} over ${grant.target_agent_name}'s data?`,
      onConfirm: async () => {
        try {
          await axios.delete(`${domain}crm/crm-access-grants/${grant.id}/`, {
            headers,
          });
          loadGrants();
        } catch (err) {
          console.error("Revoke error:", err);
          toast.error("Failed to revoke grant.");
        }
      },
    });
  };

  const isExpired = (grant) =>
    grant.expires_at && new Date(grant.expires_at) < new Date();

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Access Grants
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Let one agent temporarily view, call-log, or edit another agent&apos;s
            CRM data. Cross-agent access is view-only unless edit_contacts is
            granted explicitly; play_recording is separate from view_calls.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openAddDialog}
          sx={{
            backgroundColor: "#84DEB8",
            color: "#000",
            "&:hover": { backgroundColor: "#6cc9a1" },
          }}
        >
          Add Grant
        </Button>
      </Box>

      {loading ? (
        <Skeleton variant="rectangular" height={240} />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Grantee</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Target Agent</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Scope</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Expires</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grants.map((grant) => (
                <TableRow key={grant.id}>
                  <TableCell>{grant.grantee_name}</TableCell>
                  <TableCell>{grant.target_agent_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={SCOPES.find((s) => s.value === grant.scope)?.label || grant.scope}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {grant.expires_at ? (
                      <Chip
                        label={new Date(grant.expires_at).toLocaleString()}
                        size="small"
                        color={isExpired(grant) ? "default" : "success"}
                      />
                    ) : (
                      <Chip label="Never" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleRevoke(grant)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {grants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No access grants yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ConfirmDialog />

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Add Access Grant
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Grantee (who gets access)"
            value={form.grantee}
            onChange={(e) => setForm({ ...form, grantee: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          >
            {members.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.username}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Target Agent (whose data is shared)"
            value={form.target_agent}
            onChange={(e) => setForm({ ...form, target_agent: e.target.value })}
            sx={{ mb: 2 }}
          >
            {members.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.username}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Scopes
          </Typography>
          <FormGroup sx={{ mb: 2 }}>
            {SCOPES.map((s) => (
              <FormControlLabel
                key={s.value}
                control={
                  <Checkbox
                    checked={form.scopes.includes(s.value)}
                    onChange={() => toggleScope(s.value)}
                  />
                }
                label={s.label}
              />
            ))}
          </FormGroup>
          <TextField
            fullWidth
            type="datetime-local"
            label="Expires at (optional)"
            InputLabelProps={{ shrink: true }}
            value={form.expires_at}
            onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} startIcon={<Cancel />} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Done />}
            onClick={handleSave}
            sx={{
              backgroundColor: "#84DEB8",
              color: "#000",
              "&:hover": { backgroundColor: "#6cc9a1" },
            }}
          >
            Grant
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
